export interface AdvisorCountryParams {
  advisor_country?: string
  advisor_usage_location?: string
}

function countryCode(value: unknown): string | undefined {
  if (typeof value !== 'string' || !/^[a-z]{2}$/i.test(value.trim())) {
    return undefined
  }
  return value.trim().toUpperCase()
}

// Securitas companies use national mail domains such as securitas.de or protectas.ch.
// Group-wide (securitas.com), onmicrosoft.com and non-Securitas guest addresses carry no country.
export function emailDomainCountry(address: unknown): string | undefined {
  if (typeof address !== 'string' || !address.includes('@')) {
    return undefined
  }
  const domain = address.slice(address.lastIndexOf('@') + 1).trim().toLowerCase()
  if (!/securitas|protectas/.test(domain)) {
    return undefined
  }
  // ISO 3166 uses GB for .uk, and .eu is not a country.
  const tld = domain.split('.').pop()
  if (tld === 'eu') {
    return undefined
  }
  return countryCode(tld === 'uk' ? 'GB' : tld)
}

export function advisorCountryParams(
  value: { advisor_country?: unknown, advisor_usage_location?: unknown } | undefined,
): AdvisorCountryParams {
  const country = countryCode(value?.advisor_country)
  const usageLocation = countryCode(value?.advisor_usage_location)
  return {
    ...(country ? { advisor_country: country } : {}),
    ...(usageLocation ? { advisor_usage_location: usageLocation } : {}),
  }
}

export async function fetchAdvisorCountry(
  acquireToken: () => Promise<string | null | undefined>,
): Promise<AdvisorCountryParams> {
  try {
    const token = await acquireToken()
    if (!token) {
      return {}
    }
    const response = await fetch('https://graph.microsoft.com/v1.0/me?$select=mail,userPrincipalName,usageLocation', {
      headers: { Authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout(5000),
    })
    if (!response.ok) {
      return {}
    }
    const profile = await response.json()
    // Only the derived country codes are kept; the addresses are discarded here.
    return advisorCountryParams({
      advisor_country: emailDomainCountry(profile?.mail) ?? emailDomainCountry(profile?.userPrincipalName),
      advisor_usage_location: profile?.usageLocation,
    })
  }
  catch {
    // Optional analytics metadata must never prevent login or expose profile data.
    return {}
  }
}
